/**
 * workflow-builder-store.service.ts
 *
 * Pure RxJS state container for the workflow builder v2.
 *
 * Immutability contract (critical for OnPush components):
 *   Every mutation produces a NEW model object AND new object references along
 *   the changed path (structural sharing).  Unchanged transitions/endStates keep
 *   their original reference so OnPush checks correctly skip unchanged subtrees.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  IBuilderModel,
  ITransition,
  IEndState,
  IAction,
  IGuardClause,
  IAuthorisation,
  Locale,
} from '../models/workflow-builder.model';
import {
  fromJson,
  toJson,
  deriveStates,
  endStateFrom,
  endStateToJson,
  transitionToJson,
} from '../core/workflow-mapper';

// ─── Empty model sentinel ────────────────────────────────────────────────────

const EMPTY_MODEL: IBuilderModel = { transitions: [], states: [], topRaw: {} };

// ─── ID counter (independent of mapper's counter) ───────────────────────────

let _storeSeq = 0;
const nextStoreId = (): string => `s${++_storeSeq}`;

// ─────────────────────────────────────────────────────────────────────────────

const parseObjectArg = (value: unknown): Record<string, unknown> | unknown => {
  if (typeof value !== 'string' || !value.trim()) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : value;
  } catch {
    return value;
  }
};

const templateCodeFromValue = (value: unknown): string | null => {
  if (typeof value === 'string') return value.trim() || null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const template = value as Record<string, unknown>;
  return (
    String(
      template['code'] ??
        template['certificateTemplateCode'] ??
        template['templateCode'] ??
        template['name'] ??
        ''
    ).trim() || null
  );
};

const templateNameFromValue = (
  value: unknown,
  fallback: string
): string | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const template = value as Record<string, unknown>;
  return (
    String(
      template['certificateTemplateName'] ??
        template['certificateName'] ??
        template['title'] ??
        template['name'] ??
        fallback
    ).trim() || null
  );
};

const templateLocaleFromValue = (value: unknown, fallback: string): string => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return fallback;
  }
  const template = value as Record<string, unknown>;
  return (
    String(
      template['templateLanguage'] ??
        template['language'] ??
        template['locale'] ??
        fallback
    ).trim() || fallback
  );
};

const normalizeCertificateTemplateCodes = (
  value: unknown
): {
  codes: Record<string, string> | null;
  names: Record<string, string>;
} => {
  const parsed = parseObjectArg(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    const code = templateCodeFromValue(parsed);
    return code
      ? { codes: { 'en-US': code }, names: {} }
      : { codes: null, names: {} };
  }

  const directCode = templateCodeFromValue(parsed);
  if (directCode) {
    const locale = templateLocaleFromValue(parsed, 'en-US');
    return {
      codes: { [locale]: directCode },
      names: {
        [locale]: templateNameFromValue(parsed, directCode) || directCode,
      },
    };
  }

  const codes: Record<string, string> = {};
  const names: Record<string, string> = {};
  Object.entries(parsed as Record<string, unknown>).forEach(
    ([locale, item]) => {
      const code = templateCodeFromValue(item);
      if (!code) return;
      codes[locale] = code;
      const name = templateNameFromValue(item, code);
      if (name) names[locale] = name;
    }
  );

  return Object.keys(codes).length ? { codes, names } : { codes: null, names };
};

const normalizeGenerateCertificateArgs = (
  args: Record<string, unknown> | null
): Record<string, unknown> | null => {
  if (!args) return args;
  const normalized: Record<string, unknown> = { ...args };
  normalized['certificateNames'] = parseObjectArg(
    normalized['certificateNames']
  );
  normalized['reminderNotifications'] = parseObjectArg(
    normalized['reminderNotifications']
  );

  const expires =
    normalized['certificateExpires'] === true ||
    normalized['certificateExpires'] === 'true' ||
    (normalized['certificateExpires'] === undefined &&
      (Number(normalized['certificateExpirationDays'] ?? 0) > 0 ||
        !!normalized['reminderNotifications']));
  delete normalized['certificateExpires'];

  const template = normalizeCertificateTemplateCodes(
    normalized['certificateTemplateCode']
  );
  if (template.codes) {
    normalized['certificateTemplateCode'] = template.codes;
    if (
      (!normalized['certificateNames'] ||
        typeof normalized['certificateNames'] !== 'object' ||
        Array.isArray(normalized['certificateNames'])) &&
      Object.keys(template.names).length
    ) {
      normalized['certificateNames'] = template.names;
    }
  }

  if (
    (!normalized['certificateName'] ||
      !String(normalized['certificateName']).trim()) &&
    normalized['certificateNames'] &&
    typeof normalized['certificateNames'] === 'object' &&
    !Array.isArray(normalized['certificateNames'])
  ) {
    normalized['certificateName'] =
      Object.values(normalized['certificateNames'] as Record<string, unknown>)
        .map(value => String(value ?? '').trim())
        .find(Boolean) || '';
  }

  if (!expires) {
    normalized['certificateExpirationDays'] = 0;
    delete normalized['reminderOffsetDays'];
    delete normalized['reminderNotifications'];
  } else if (
    normalized['certificateExpirationDays'] === undefined ||
    normalized['certificateExpirationDays'] === null ||
    normalized['certificateExpirationDays'] === '' ||
    Number(normalized['certificateExpirationDays']) <= 0
  ) {
    normalized['certificateExpirationDays'] = 365;
  }

  return normalized;
};

@Injectable()
export class WorkflowBuilderStoreService {
  // ─── State ──────────────────────────────────────────────────────────────────

  private readonly _model$ = new BehaviorSubject<IBuilderModel>(EMPTY_MODEL);
  private readonly _parseError$ = new BehaviorSubject<string | null>(null);
  private readonly _canUndo$ = new BehaviorSubject(false);
  private readonly _canRedo$ = new BehaviorSubject(false);
  private readonly historyLimit = 20;
  private past: IBuilderModel[] = [];
  private future: IBuilderModel[] = [];

  // ─── Public observables ──────────────────────────────────────────────────────

  /** Read-only stream of the current builder model. */
  readonly model$: Observable<IBuilderModel> = this._model$.asObservable();

  /** Derived JSON representation of the model. */
  readonly json$: Observable<any> = this._model$.pipe(map(toJson));

  /** Emits a parse-error message when load() fails, null otherwise. */
  readonly parseError$: Observable<string | null> =
    this._parseError$.asObservable();
  readonly canUndo$: Observable<boolean> = this._canUndo$.asObservable();
  readonly canRedo$: Observable<boolean> = this._canRedo$.asObservable();

  get canUndo(): boolean {
    return this.past.length > 0;
  }

  get canRedo(): boolean {
    return this.future.length > 0;
  }

  // ─── load ────────────────────────────────────────────────────────────────────

  /**
   * Parse a workflow JSON document into the store.
   * On failure, resets to EMPTY_MODEL and emits a message on parseError$.
   */
  load(json: any): void {
    this.resetHistory();
    try {
      const model = fromJson(json);
      this._parseError$.next(null);
      this._model$.next(model);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to parse workflow JSON';
      this._parseError$.next(msg || 'Failed to parse workflow JSON');
      this._model$.next(EMPTY_MODEL);
    }
  }

  undo(): boolean {
    const previous = this.past.pop();
    if (!previous) return false;
    this.future.push(this.cloneModel(this._model$.value));
    this._model$.next(previous);
    this.emitHistoryState();
    return true;
  }

  redo(): boolean {
    const next = this.future.pop();
    if (!next) return false;
    this.past.push(this.cloneModel(this._model$.value));
    this._model$.next(next);
    this.emitHistoryState();
    return true;
  }

  // ─── addTransition ───────────────────────────────────────────────────────────

  /**
   * Append a new transition.
   * Returns `false` (no-op, same reference) if a transition with the same
   * (startState, event) already exists; `true` when a transition was added.
   */
  addTransition(input: {
    startState: string;
    event: string;
    toStateCode: string;
    who?: string;
    auth?: IAuthorisation;
  }): boolean {
    const current = this._model$.value;

    // Duplicate check
    const duplicate = current.transitions.some(
      t => t.startState === input.startState && t.event === input.event
    );
    if (duplicate) return false; // no-op, no emit

    const newEndState: IEndState = {
      stateCode: input.toStateCode,
      actions: [],
      nextEvent: null,
      raw: { stateName: input.toStateCode },
    };

    const newTransition: ITransition = {
      id: nextStoreId(),
      startState: input.startState,
      event: input.event,
      eventNames: null,
      authorisation: input.auth ?? null,
      one: newEndState,
      two: null,
      guard: null,
    };

    const newTransitions = [...current.transitions, newTransition];
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
    return true;
  }

  // ─── copyTransitionTo ──────────────────────────────────────────────────────────

  /**
   * Clone an existing transition onto another start state — the "copy event to a state"
   * feature. The clone gets a fresh id and fresh action nodes (no `raw`/position, so the
   * mapper re-serialises it cleanly) and keeps the source event, authorisation, guard,
   * entry actions and destination(s).
   *
   * Returns `false` (no-op) if the source transition is missing or the target state
   * already has a transition with the same event (start + event must be unique).
   */
  copyTransitionTo(sourceTid: string, targetStateCode: string): boolean {
    const current = this._model$.value;
    const src = current.transitions.find(t => t.id === sourceTid);
    if (!src || !targetStateCode) return false;
    if (
      current.transitions.some(
        t => t.startState === targetStateCode && t.event === src.event
      )
    )
      return false;

    const newTransitions = [
      ...current.transitions,
      this._cloneTransition(src, targetStateCode),
    ];
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
    return true;
  }

  // ─── event clipboard (copy / paste an event onto a new event) ───────────────────

  /** Deep-cloned snapshot of a copied transition, used by paste. */
  private _eventClipboard: ITransition | null = null;
  /** Event name currently on the clipboard (for paste-button labels), or null. */
  get clipboardEvent(): string | null {
    return this._eventClipboard?.event ?? null;
  }

  /**
   * The full copied transition (a deep-cloned snapshot) so the composer can prefill its
   * event / authorisation / destination fields for the edit-before-create paste. Read-only:
   * callers must not mutate it.
   */
  get clipboardTransition(): ITransition | null {
    return this._eventClipboard;
  }

  /** Copy a transition's full config to the clipboard (survives later edits/deletes). */
  copyEvent(tid: string): boolean {
    const t = this._model$.value.transitions.find(x => x.id === tid);
    if (!t) return false;
    this._eventClipboard = this._cloneTransition(t, t.startState);
    return true;
  }

  /**
   * Paste the clipboard event onto `targetStateCode` — creates a transition there with the
   * copied event, authorisation, guard, entry actions and destination(s). No-op (false) if
   * the clipboard is empty or the target already has a transition with that event.
   */
  pasteEventOnto(targetStateCode: string): boolean {
    const clip = this._eventClipboard;
    if (!clip || !targetStateCode) return false;
    const current = this._model$.value;
    if (
      current.transitions.some(
        t => t.startState === targetStateCode && t.event === clip.event
      )
    )
      return false;

    const newTransitions = [
      ...current.transitions,
      this._cloneTransition(clip, targetStateCode),
    ];
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
    return true;
  }

  /**
   * Edit-before-create paste: create a transition on `input.startState` from the copied
   * clipboard event, KEEPING the copied actions / guard / outcome structure but applying the
   * edits the user made in the composer before creation — the event name, authorisation, and
   * the (possibly re-pointed) destination state code(s).
   *
   * Like a move (`setEndStateCode`), re-pointing here PRESERVES the outcome's actions —
   * carrying the actions over is the whole point of a copy. `_cloneTransition` already strips
   * `raw`, so the re-pointed outcome serialises cleanly under its new state code.
   *
   * Returns `false` (no-op, no emit) when the clipboard is empty, a required field is missing,
   * or `(startState, event)` already exists — so the caller can BLOCK with a message rather
   * than silently do nothing.
   */
  pasteEventEdited(input: {
    startState: string;
    event: string;
    auth: IAuthorisation | null;
    oneStateCode: string;
    twoStateCode?: string | null;
  }): boolean {
    const clip = this._eventClipboard;
    if (!clip || !input.startState || !input.event || !input.oneStateCode)
      return false;
    const current = this._model$.value;
    if (
      current.transitions.some(
        t => t.startState === input.startState && t.event === input.event
      )
    )
      return false;

    const clone = this._cloneTransition(clip, input.startState);
    const repoint = (es: IEndState, code: string): IEndState => ({
      ...es,
      stateCode: code,
    });
    const built: ITransition = {
      ...clone,
      event: input.event,
      authorisation: input.auth,
      one: repoint(clone.one, input.oneStateCode),
      two:
        clone.two && input.twoStateCode
          ? repoint(clone.two, input.twoStateCode)
          : clone.two,
    };
    const newTransitions = [...current.transitions, built];
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
    return true;
  }

  /** Deep-clone a transition onto a (possibly different) start state, with a fresh id and no `raw`. */
  private _cloneTransition(src: ITransition, startState: string): ITransition {
    const cloneEndState = (es: IEndState): IEndState => ({
      stateCode: es.stateCode,
      actions: es.actions.map(a => ({
        actionType: a.actionType,
        required: a.required,
        args: a.args != null ? JSON.parse(JSON.stringify(a.args)) : null,
      })),
      nextEvent: es.nextEvent,
    });
    return {
      id: nextStoreId(),
      startState,
      event: src.event,
      eventNames: src.eventNames ? { ...src.eventNames } : null,
      authorisation: src.authorisation ? { ...src.authorisation } : null,
      one: cloneEndState(src.one),
      two: src.two ? cloneEndState(src.two) : null,
      guard: src.guard
        ? { clauses: src.guard.clauses.map(c => ({ ...c })) }
        : null,
    };
  }

  // ─── deleteTransition ────────────────────────────────────────────────────────

  /** Remove a transition by id. No-op if id is not found. */
  deleteTransition(id: string): void {
    const current = this._model$.value;
    const idx = current.transitions.findIndex(t => t.id === id);
    if (idx === -1) return; // no-op

    // Structural sharing: keep all other transition references intact
    const newTransitions = current.transitions.filter(t => t.id !== id);
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
  }

  // ─── deleteState ─────────────────────────────────────────────────────────────

  /**
   * Remove a state entirely: drops every transition where the state is the start OR an
   * end (outgoing events from it AND incoming events that lead to it), so the node and its
   * connectors disappear. No-op if no transition references the code.
   */
  deleteState(code: string): void {
    const current = this._model$.value;
    const newTransitions = current.transitions.filter(
      t =>
        t.startState !== code &&
        t.one.stateCode !== code &&
        !(t.two != null && t.two.stateCode === code)
    );
    if (newTransitions.length === current.transitions.length) return; // no-op
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
  }

  // ─── addAction ───────────────────────────────────────────────────────────────

  /**
   * Append a new IAction to the endState specified by `which` ('one' | 'two').
   * No-op if the transition or endState is not found.
   */
  addAction(
    tid: string,
    which: 'one' | 'two',
    actionType: string,
    required: boolean,
    callbackStateCode?: string | null
  ): void {
    const current = this._model$.value;
    const idx = current.transitions.findIndex(t => t.id === tid);
    if (idx === -1) return;
    const target = current.transitions[idx];
    const targetEndState = which === 'two' ? target.two : target.one;
    if (!targetEndState) return;

    const newAction: IAction = {
      actionType,
      required,
      args: actionType === 'INTEGRATION' ? { async: true } : null,
    };
    const updatedEndState = {
      ...targetEndState,
      actions: [...targetEndState.actions, newAction],
    };
    const updatedTransition =
      which === 'two'
        ? { ...target, two: updatedEndState }
        : { ...target, one: updatedEndState };
    const withAction = current.transitions.map((transition, transitionIndex) =>
      transitionIndex === idx ? updatedTransition : transition
    );
    const transitions =
      this.isAsyncIntegrationAction(newAction) && targetEndState.stateCode
        ? this.ensurePushCallbackTransition(
            withAction,
            targetEndState.stateCode,
            callbackStateCode
          )
        : withAction;

    this.commit({
      ...current,
      transitions,
      states: deriveStates(transitions),
    });
  }

  /**
   * Duplicate an existing action (from any state) into the endState specified by
   * `which`. The copy is a fresh node (no `raw`), so the mapper re-serialises it
   * cleanly. No-op if the target transition/endState is not found.
   */
  addActionCopy(tid: string, which: 'one' | 'two', source: IAction): void {
    const copy: IAction = {
      actionType: source.actionType,
      required: source.required,
      args:
        source.args != null ? JSON.parse(JSON.stringify(source.args)) : null,
    };
    this._updateEndState(tid, which, es => ({
      ...es,
      actions: [...es.actions, copy],
    }));
  }

  // ─── action clipboard (copy / paste a single action onto another state) ─────────

  /** Deep-cloned snapshot of a copied action (survives later edits/deletes). */
  private _actionClipboard: IAction | null = null;
  /** The action currently on the clipboard (read-only snapshot), or null. */
  get clipboardAction(): IAction | null {
    return this._actionClipboard;
  }

  /** Copy a single action to the clipboard so it can be pasted onto another state. */
  copyAction(source: IAction): void {
    this._actionClipboard = {
      actionType: source.actionType,
      required: source.required,
      args:
        source.args != null ? JSON.parse(JSON.stringify(source.args)) : null,
    };
  }

  /**
   * Paste the clipboard action onto the endState specified by `which`, appending it
   * (a fresh copy) alongside whatever actions the target already has. No-op (false) if
   * the clipboard is empty or the transition/endState is not found.
   */
  pasteActionOnto(tid: string, which: 'one' | 'two'): boolean {
    if (!this._actionClipboard) return false;
    this.addActionCopy(tid, which, this._actionClipboard);
    return true;
  }

  // ─── removeAction ────────────────────────────────────────────────────────────

  /** Remove the action at `index` from the specified endState. */
  removeAction(tid: string, which: 'one' | 'two', index: number): void {
    this._updateEndState(tid, which, es => ({
      ...es,
      actions: es.actions.filter((_, i) => i !== index),
    }));
  }

  // ─── setNextEvent ────────────────────────────────────────────────────────────

  /**
   * Set the event the SYSTEM runs automatically after landing on this end state (chaining).
   * Pass null/'' to clear. Mirrors into raw so the serialised JSON stays consistent.
   */
  setNextEvent(
    tid: string,
    which: 'one' | 'two',
    nextEvent: string | null
  ): void {
    const ne = nextEvent && nextEvent.trim() ? nextEvent.trim() : null;
    this._updateEndState(tid, which, es => ({
      ...es,
      nextEvent: ne,
      raw: es.raw ? { ...es.raw, nextEvent: ne } : es.raw,
    }));
  }

  // ─── toggleRequired ──────────────────────────────────────────────────────────

  /** Toggle the `required` flag on the action at `index`. */
  toggleRequired(tid: string, which: 'one' | 'two', index: number): void {
    this._updateEndState(tid, which, es => ({
      ...es,
      actions: es.actions.map((a, i) =>
        i === index ? { ...a, required: !a.required } : a
      ),
    }));
  }

  // ─── setActionArgs ───────────────────────────────────────────────────────────

  /** Set `args` on the action at `index`. */
  setActionArgs(
    tid: string,
    which: 'one' | 'two',
    index: number,
    args: Record<string, unknown> | null,
    callbackStateCode?: string | null
  ): void {
    const current = this._model$.value;
    const transitionIndex = current.transitions.findIndex(t => t.id === tid);
    if (transitionIndex === -1) return;
    const target = current.transitions[transitionIndex];
    const targetEndState = which === 'two' ? target.two : target.one;
    if (!targetEndState) return;

    let updatedAction: IAction | null = null;
    const updatedEndState = {
      ...targetEndState,
      actions: targetEndState.actions.map((action, actionIndex) => {
        if (actionIndex !== index) return action;
        const normalizedArgs =
          action.actionType === 'GENERATE_CERTIFICATE'
            ? normalizeGenerateCertificateArgs(args)
            : args;
        updatedAction = { ...action, args: normalizedArgs };
        return updatedAction;
      }),
    };
    const updatedTransition =
      which === 'two'
        ? { ...target, two: updatedEndState }
        : { ...target, one: updatedEndState };
    const withArgs = current.transitions.map((transition, indexInList) =>
      indexInList === transitionIndex ? updatedTransition : transition
    );
    let transitions = withArgs;
    if (updatedAction && targetEndState.stateCode) {
      if (this.isAsyncIntegrationAction(updatedAction)) {
        transitions = this.ensurePushCallbackTransition(
          withArgs,
          targetEndState.stateCode,
          callbackStateCode
        );
      } else if (this.isIntegrationAction(updatedAction)) {
        transitions = this.removePushCallbackTransitionIfUnused(
          withArgs,
          targetEndState.stateCode
        );
      }
    }

    this.commit({
      ...current,
      transitions,
      states: deriveStates(transitions),
    });
  }

  /** Serialize one endState to its JSON node (for the per-state JSON editor). */
  endStateJson(tid: string, which: 'one' | 'two'): unknown {
    const t = this._model$.value.transitions.find(x => x.id === tid);
    const es = t ? (which === 'two' ? t.two : t.one) : null;
    return es ? endStateToJson(es) : null;
  }

  /** Replace an endState from an edited JSON node (per-state JSON editor "Apply"). */
  replaceEndState(tid: string, which: 'one' | 'two', node: unknown): void {
    const es = endStateFrom(node as any);
    this._updateTransition(tid, t =>
      which === 'two' ? { ...t, two: es } : { ...t, one: es ?? t.one }
    );
  }

  /** Serialize a WHOLE transition to its full workflow JSON item (event → everything). */
  transitionJson(tid: string): unknown {
    const t = this._model$.value.transitions.find(x => x.id === tid);
    return t ? transitionToJson(t) : null;
  }

  /**
   * Replace a whole transition from an edited workflow JSON item (the state view's JSON
   * editor). Parses the node via fromJson, keeps the original transition id so the open
   * inspector reference stays valid, then re-derives states.
   */
  replaceTransition(tid: string, node: unknown): void {
    const current = this._model$.value;
    const idx = current.transitions.findIndex(t => t.id === tid);
    if (idx === -1) return;
    const parsed = fromJson({ workflow: [node] }).transitions[0];
    if (!parsed) return;
    const replaced = { ...parsed, id: tid };
    const newTransitions = current.transitions.map((t, i) =>
      i === idx ? replaced : t
    );
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
  }

  // ─── reorderActions ──────────────────────────────────────────────────────────

  /** Move the action at `from` to position `to` (splice-style). */
  reorderActions(
    tid: string,
    which: 'one' | 'two',
    from: number,
    to: number
  ): void {
    this._updateEndState(tid, which, es => {
      const actions = [...es.actions];
      const [item] = actions.splice(from, 1);
      actions.splice(to, 0, item);
      return { ...es, actions };
    });
  }

  /**
   * Split a transition into TWO outcomes by adding endStateTwo + a guard clause.
   * The guard's clauses decide endStateTwo (✓ true); endStateOne is the ✕ false
   * path. endStateTwo seeds from endStateOne's stateCode (re-point it via the
   * state config / per-state JSON). No-op if a second outcome already exists.
   */
  addCondition(tid: string): void {
    this._updateTransition(tid, t => {
      if (t.two) return t;
      const two: IEndState = {
        stateCode: t.one.stateCode,
        actions: [],
        nextEvent: null,
      };
      return {
        ...t,
        two,
        guard: t.guard ?? {
          clauses: [{ parameter: '', comparator: 'EQ', value: '' }],
        },
      };
    });
  }

  /** Remove the second outcome entirely (drops endStateTwo + guard). */
  removeCondition(tid: string): void {
    // Also drop the preserved raw `endStateTwo` / `endStateCondition`, otherwise the mapper
    // falls back to them and keeps serialising the now-removed second outcome.
    this._updateTransition(tid, t => ({
      ...t,
      two: null,
      guard: null,
      raw: t.raw
        ? { ...t.raw, endStateTwo: null, endStateCondition: null }
        : t.raw,
    }));
  }

  // ─── setGuardClauses / removeGuard ──────────────────────────────────────────

  /**
   * Redirect an outcome (one or two) to a different destination state.
   *
   * By default the outcome's OWN configuration — its actions and chained `nextEvent` —
   * travels with it: re-pointing where an event lands should not silently discard the work
   * configured on that outcome (BILL_ID_GENERATION, notifications, etc.). Pass
   * `carryActions = false` to instead land on a bare state (used when the author explicitly
   * chooses to drop the actions in the move confirmation).
   */
  setEndStateCode(
    tid: string,
    which: 'one' | 'two',
    stateCode: string,
    carryActions = true
  ): void {
    if (!stateCode) return;
    const current = this._model$.value;
    const idx = current.transitions.findIndex(t => t.id === tid);
    if (idx === -1) return;
    const t = current.transitions[idx];
    const es = which === 'two' ? t.two : t.one;
    if (!es || (which === 'two' && !t.two)) return;
    const updated: IEndState = {
      ...es,
      stateCode,
      ...(carryActions ? {} : { actions: [], nextEvent: null }),
      raw: es.raw ? { ...es.raw, stateCode, stateName: stateCode } : undefined,
    };
    const newT =
      which === 'two' ? { ...t, two: updated } : { ...t, one: updated };
    const newTransitions = current.transitions.map((x, i) =>
      i === idx ? newT : x
    );
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
  }

  /**
   * Re-anchor a transition's SOURCE onto a different existing state (the "move the arrow
   * by its tail" gesture). Unlike re-pointing an outcome, the event and all its actions
   * travel with the arrow — only where it leaves from changes.
   *
   * Blocks (returns false, no mutation) when the destination state already owns an event
   * with the same name, so the move can never create an ambiguous duplicate `(start, event)`.
   * No-op returns false for an unknown id or an unchanged start state.
   */
  setStartState(tid: string, stateCode: string): boolean {
    if (!stateCode) return false;
    const current = this._model$.value;
    const idx = current.transitions.findIndex(t => t.id === tid);
    if (idx === -1) return false;
    const t = current.transitions[idx];
    if (t.startState === stateCode) return false;
    const clash = current.transitions.some(
      x => x.id !== tid && x.startState === stateCode && x.event === t.event
    );
    if (clash) return false;
    const newT: ITransition = { ...t, startState: stateCode };
    const newTransitions = current.transitions.map((x, i) =>
      i === idx ? newT : x
    );
    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
    return true;
  }

  /** Set the guard clauses on the transition. */
  setGuardClauses(tid: string, clauses: IGuardClause[]): void {
    this._updateTransition(tid, t => ({
      ...t,
      guard: { clauses },
    }));
  }

  /** Clear the guard on the transition (sets guard = null). */
  removeGuard(tid: string): void {
    this._updateTransition(tid, t => ({
      ...t,
      guard: null,
    }));
  }

  // ─── setAuthorisation / setEventNames ────────────────────────────────────────

  /** Set (or clear) the authorisation on the transition. */
  setAuthorisation(tid: string, auth: IAuthorisation | null): void {
    this._updateTransition(tid, t => ({
      ...t,
      authorisation: auth,
    }));
  }

  /** Set the eventNames map on the transition. */
  setEventNames(tid: string, names: Partial<Record<Locale, string>>): void {
    this._updateTransition(tid, t => ({
      ...t,
      eventNames: names,
    }));
  }

  // ─── setPosition ─────────────────────────────────────────────────────────────

  /**
   * Write a position into the `raw.position` of every endState whose
   * `stateCode` matches.  This is the only mutation that touches `raw`; it
   * ensures position round-trips through `toJson` because `endStateToJson`
   * spreads the raw clone.
   *
   * No-op (same model reference) if the stateCode is not found.
   */
  setPosition(stateCode: string, pos: { x: number; y: number }): void {
    const current = this._model$.value;

    // Check whether ANY endState matches before doing any work
    const affected = current.transitions.some(
      t =>
        t.one.stateCode === stateCode ||
        (t.two != null && t.two.stateCode === stateCode)
    );
    if (!affected) return; // no-op

    const newTransitions = current.transitions.map(t => {
      const oneMatch = t.one.stateCode === stateCode;
      const twoMatch = t.two != null && t.two.stateCode === stateCode;

      if (!oneMatch && !twoMatch) return t; // structural sharing

      const newOne: IEndState = oneMatch
        ? { ...t.one, raw: { ...(t.one.raw ?? {}), position: pos } }
        : t.one;

      const newTwo: IEndState | null =
        twoMatch && t.two != null
          ? { ...t.two, raw: { ...(t.two.raw ?? {}), position: pos } }
          : t.two;

      return { ...t, one: newOne, two: newTwo };
    });

    this.commit({
      ...current,
      transitions: newTransitions,
    });
  }

  /** Persist one visual outcome card without moving other occurrences of its state code. */
  setEndStatePosition(
    tid: string,
    which: 'one' | 'two',
    pos: { x: number; y: number }
  ): void {
    this._updateEndState(tid, which, endState => ({
      ...endState,
      raw: { ...(endState.raw ?? {}), position: pos },
    }));
  }

  /**
   * Batch-write positions for many states in a single model update (used by
   * "Tidy up"). Each matching endState's raw.position is set so it round-trips.
   */
  setPositions(
    map: Record<string, { x: number; y: number }>,
    outcomes: Array<{
      tid: string;
      which: 'one' | 'two';
      position: { x: number; y: number };
    }> = []
  ): void {
    const current = this._model$.value;
    const exact = new Map(
      outcomes.map(item => [`${item.tid}:${item.which}`, item.position])
    );
    const newTransitions = current.transitions.map(t => {
      const po = exact.get(`${t.id}:one`) ?? map[t.one.stateCode];
      const pt = t.two
        ? exact.get(`${t.id}:two`) ?? map[t.two.stateCode]
        : undefined;
      const newOne: IEndState = po
        ? { ...t.one, raw: { ...(t.one.raw ?? {}), position: po } }
        : t.one;
      const newTwo: IEndState | null =
        t.two && pt
          ? { ...t.two, raw: { ...(t.two.raw ?? {}), position: pt } }
          : t.two;
      if (newOne === t.one && newTwo === t.two) return t;
      return { ...t, one: newOne, two: newTwo };
    });
    this.commit({ ...current, transitions: newTransitions });
  }

  /**
   * Rename a workflow state by code and label.
   *
   * State identity is referenced in two places: as endState.stateCode on incoming
   * transitions and as transition.startState on outgoing transitions. Updating
   * both keeps the graph connected when a state code changes.
   */
  setStateIdentity(
    previousCode: string,
    input: {
      stateCode: string;
      stateName: string;
      stateNames?: Record<string, string> | null;
    }
  ): void {
    const nextCode = input.stateCode.trim();
    if (!previousCode || !nextCode) return;

    const nextName = input.stateName.trim() || nextCode;
    const current = this._model$.value;
    const affected = current.transitions.some(
      t =>
        t.startState === previousCode ||
        t.one.stateCode === previousCode ||
        (t.two != null && t.two.stateCode === previousCode)
    );
    if (!affected) return;

    const renameEndState = (es: IEndState): IEndState => {
      if (es.stateCode !== previousCode) return es;
      const raw: Record<string, unknown> = {
        ...(es.raw ?? {}),
        stateCode: nextCode,
        stateName: nextName,
      };
      if ('stateNames' in input) {
        raw['stateNames'] = input.stateNames ?? null;
      }
      return {
        ...es,
        stateCode: nextCode,
        raw,
      };
    };

    const newTransitions = current.transitions.map(t => {
      const one = renameEndState(t.one);
      const two = t.two != null ? renameEndState(t.two) : t.two;
      const startState =
        t.startState === previousCode ? nextCode : t.startState;
      if (one === t.one && two === t.two && startState === t.startState)
        return t;
      return { ...t, startState, one, two };
    });

    this.commit({
      ...current,
      transitions: newTransitions,
      states: deriveStates(newTransitions),
    });
  }

  // ─── Private helpers ─────────────────────────────────────────────────────────

  private commit(model: IBuilderModel): void {
    if (model === this._model$.value) return;
    this.past.push(this.cloneModel(this._model$.value));
    if (this.past.length > this.historyLimit) this.past.shift();
    this.future = [];
    this._model$.next(model);
    this.emitHistoryState();
  }

  private resetHistory(): void {
    this.past = [];
    this.future = [];
    this.emitHistoryState();
  }

  private emitHistoryState(): void {
    this._canUndo$.next(this.canUndo);
    this._canRedo$.next(this.canRedo);
  }

  private cloneModel(model: IBuilderModel): IBuilderModel {
    return JSON.parse(JSON.stringify(model)) as IBuilderModel;
  }

  /**
   * Core structural-sharing helper.
   * Maps the transitions array: for the transition matching `tid`, applies `fn`
   * to produce a new transition object; all other transitions keep their reference.
   * If `tid` is not found, the call is a no-op (model reference unchanged).
   */
  private _updateTransition(
    tid: string,
    fn: (t: ITransition) => ITransition
  ): void {
    const current = this._model$.value;
    const idx = current.transitions.findIndex(t => t.id === tid);
    if (idx === -1) return;

    const updated = fn(current.transitions[idx]);
    if (updated === current.transitions[idx]) return;
    const newTransitions = current.transitions.map((t, i) =>
      i === idx ? updated : t
    );
    this.commit({ ...current, transitions: newTransitions });
  }

  /**
   * Helper: update a specific endState (`one` or `two`) within a transition.
   * Produces new endState + transition references; all other transitions unchanged.
   * No-op if `which` is `'two'` and `t.two` is null.
   */
  private _updateEndState(
    tid: string,
    which: 'one' | 'two',
    fn: (es: IEndState) => IEndState
  ): void {
    this._updateTransition(tid, t => {
      if (which === 'two') {
        if (t.two == null) return t; // no-op — no second endState
        return { ...t, two: fn(t.two) };
      }
      return { ...t, one: fn(t.one) };
    });
  }

  private isAsyncIntegrationAction(action: IAction): boolean {
    return (
      this.isIntegrationAction(action) &&
      !!(action.args as Record<string, unknown> | null)?.['async']
    );
  }

  private isIntegrationAction(action: IAction): boolean {
    return action.actionType.startsWith('INTEGRATION');
  }

  private removePushCallbackTransitionIfUnused(
    transitions: ITransition[],
    stateCode: string
  ): ITransition[] {
    if (this.stateHasAsyncIntegration(transitions, stateCode)) {
      return transitions;
    }
    return transitions.filter(
      transition =>
        !(transition.startState === stateCode && transition.event === 'PUSH')
    );
  }

  private stateHasAsyncIntegration(
    transitions: ITransition[],
    stateCode: string
  ): boolean {
    return transitions.some(transition =>
      [transition.one, transition.two]
        .filter(Boolean)
        .some(
          endState =>
            endState?.stateCode === stateCode &&
            endState.actions.some(action =>
              this.isAsyncIntegrationAction(action)
            )
        )
    );
  }

  private ensurePushCallbackTransition(
    transitions: ITransition[],
    stateCode: string,
    callbackStateCode?: string | null
  ): ITransition[] {
    const completedStateCode = callbackStateCode?.trim();
    if (!completedStateCode || completedStateCode === stateCode) {
      return transitions;
    }

    let found = false;
    const updated = transitions.map(transition => {
      if (transition.startState !== stateCode || transition.event !== 'PUSH') {
        return transition;
      }
      found = true;
      if (transition.one.stateCode === completedStateCode) return transition;
      const nextEndState: IEndState = {
        ...transition.one,
        stateCode: completedStateCode,
        raw: transition.one.raw
          ? {
              ...transition.one.raw,
              stateCode: completedStateCode,
              stateName: completedStateCode,
            }
          : transition.one.raw,
      };
      return { ...transition, one: nextEndState };
    });
    if (found) return updated;

    const pushEndState: IEndState = {
      stateCode: completedStateCode,
      actions: [],
      nextEvent: null,
    };
    return [
      ...transitions,
      {
        id: nextStoreId(),
        startState: stateCode,
        event: 'PUSH',
        eventNames: null,
        authorisation: {
          systemAuthorised: true,
          authorisedRoles: null,
          authorisedUsers: null,
        },
        one: pushEndState,
        two: null,
        guard: null,
      },
    ];
  }
}
